import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col, message, Row,
  Space, Spin, Typography
} from 'antd';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import { createPositionService, getPositionByIdService, updatePositionService } from 'common/services/extends/position';
import { PositionRequestType } from 'common/services/extends/position/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { positionSchema } from 'common/utils/schemas';

interface PositionForm {
  name: string;
  code: string;
}

const defaultValues = {
  name: '',
  code: '',
};

const PositionDetail: React.FC<ActiveRoles> = ({ roleIndex, roleUpdate }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const id = Number(searchParams.get('id'));
  /* States */

  /* React-hook-form */
  const method = useForm<PositionForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(positionSchema),
  });

  const queryKey = ['position-detail', id];

  const { isLoading } = useQuery(
    queryKey,
    () => getPositionByIdService(id),
    {
      enabled: !!id && roleIndex,
      onSuccess: (data) => {
        method.reset({
          name: data.positionData?.name || '',
          code: data.positionData?.code,
        });
      },
    }
  );

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['create-position'],
    async (params: PositionRequestType) => createPositionService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(ROUTE_PATHS.POSITION_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.createError'));
      }
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['update-position'],
    async (
      params: PositionRequestType
        & { id: number }
    ) => updatePositionService({ ...params, id }),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.updateError'));
      }
    }
  );

  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) return;

    const formData = method.getValues();

    const submitData = {
      name: formData.name,
      code: formData.code,
    };

    if (id) {
      updateMutate({
        id,
        ...submitData
      });
    } else {
      createMutate(submitData);
    }
  };

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.position')}
        rightHeader={(
          <Space size={16}>
            <Button
              type="primary"
              disabled={!!id && !roleUpdate}
              onClick={method.handleSubmit(onSubmit)}
            >
              <SaveOutlined />
              {t('system.save')}
            </Button>
          </Space>
        )}
      />
      <Spin size="large" spinning={isLoading || createLoading || updateLoading}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col xxl={24}>
                <Card type="inner">
                  <div className="site-card-border-less-wrapper">
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: '100%' }}
                    >
                      <Row gutter={16}>
                        <Col span={12} className="u-mt-8">
                          <Typography.Text strong>
                            {t('system.name')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="name"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </Col>
                        <Col span={12} className="u-mt-8">
                          <Typography.Text strong>
                            {t('system.code')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="code"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                                disabled={!!id}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </Space>
                  </div>
                </Card>
              </Col>
            </Row>
          </FormProvider>
        </div>
      </Spin>
    </>
  );
};

export default PositionDetail;
